import { Link, useNavigate } from "react-router-dom";

import MeridianLockup from "../components/MeridianLockup";
import { signOut, useAuth } from "../lib/auth";

/**
 * MER-193: 403 page rendered when a signed-in non-staff user hits
 * an `/admin/*` route. Mirrors the NotFound (MER-169) brand card
 * pattern so the user sees a consistent surface and knows the page
 * is intentional, not a broken link.
 */
export default function Forbidden() {
  const navigate = useNavigate();
  const { session } = useAuth();

  const slug =
    (session?.user.user_metadata as { customer_slug?: string } | undefined)?.customer_slug ?? null;
  const backHref = slug ? `/${slug}` : "/login";

  async function handleSignOut() {
    await signOut();
    navigate("/login", { replace: true });
  }

  return (
    <main
      style={{
        display: "grid",
        placeItems: "center",
        minHeight: "100vh",
        padding: "32px",
        background:
          "radial-gradient(at 30% 20%, rgba(38,168,159,0.06), transparent 50%), var(--bg)",
      }}
    >
      <div
        style={{
          background: "var(--bg-card)",
          border: "1px solid var(--border-subtle)",
          borderRadius: 16,
          padding: "36px 36px 28px",
          width: "100%",
          maxWidth: 420,
          boxShadow: "var(--shadow-card)",
          display: "grid",
          gap: 18,
          textAlign: "center",
        }}
      >
        <div style={{ display: "grid", justifyItems: "center", gap: 14 }}>
          <MeridianLockup size="lg" align="stack" />
        </div>

        <div style={{ display: "grid", gap: 8 }}>
          <div style={{ fontSize: 48, fontWeight: 800, color: "var(--brand-deep)" }}>403</div>
          <p style={{ fontSize: 14, color: "var(--text-2)" }}>
            You don&apos;t have access to this area.
          </p>
          {session ? (
            <p style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.5 }}>
              If you were just granted staff access, sign out and back in to refresh your session.
            </p>
          ) : null}
        </div>

        <div style={{ display: "grid", gap: 10 }}>
          <Link to={backHref} style={primaryButtonStyle}>
            Back to dashboard
          </Link>
          {session ? (
            <button type="button" onClick={handleSignOut} style={secondaryButtonStyle}>
              Sign out
            </button>
          ) : null}
        </div>

        <div
          style={{
            borderTop: "1px solid var(--border-subtle)",
            marginTop: 6,
            paddingTop: 12,
          }}
        >
          <p style={{ fontSize: 12, color: "var(--text-muted)" }}>
            Need help?{" "}
            <a
              href="mailto:support@meridiananalytics.io"
              style={{ color: "var(--brand-deep)", textDecoration: "none" }}
            >
              support@meridiananalytics.io
            </a>
          </p>
        </div>
      </div>
    </main>
  );
}

const primaryButtonStyle: React.CSSProperties = {
  font: "inherit",
  fontWeight: 600,
  padding: "12px 14px",
  borderRadius: 10,
  border: "none",
  background: "linear-gradient(95deg, var(--brand-deep) 0%, var(--brand-teal) 100%)",
  color: "white",
  cursor: "pointer",
  letterSpacing: 0.3,
  textDecoration: "none",
  textAlign: "center",
  display: "block",
};

const secondaryButtonStyle: React.CSSProperties = {
  font: "inherit",
  fontWeight: 600,
  padding: "10px 14px",
  borderRadius: 10,
  border: "1px solid var(--border-subtle)",
  background: "transparent",
  color: "var(--text)",
  cursor: "pointer",
};
